import { useCallback } from 'react'
import { trackMapInteraction } from '../utils/analytics'

/**
 * Custom hook for tracking map-related analytics events
 * Provides atomic, reusable tracking functions for map interactions
 * 
 * Follows Architecture Guidelines:
 * - Atomicity: Single responsibility (map analytics only)
 * - Idempotency: Pure tracking functions, no side effects
 * - Modularity: Self-contained, no hard dependencies
 * 
 * @returns {Object} Map tracking functions
 * 
 * @example
 * const { trackMarkerClick } = useMapAnalytics()
 * <Marker onClick={() => trackMarkerClick(entity)} />
 */
export function useMapAnalytics() {
  /**
   * Track marker click with entity context
   * @param {Object} entity - Entity object with name, entityId, and id
   */
  const trackMarkerClick = useCallback((entity) => {
    trackMapInteraction('marker_click', entity.name, {
      entity_id: entity.entityId,
      entity_type: 'vendor',
      sighting_id: entity.id
    })
  }, [])

  /**
   * Track map zoom
   * @param {number} zoomLevel - New zoom level
   * @param {string} trigger - What triggered the zoom ('user', 'programmatic', 'bounds')
   */
  const trackMapZoom = useCallback((zoomLevel, trigger = 'user') => {
    trackMapInteraction('zoom', null, {
      zoom_level: zoomLevel,
      trigger
    })
  }, [])

  /**
   * Track map pan/drag
   * @param {Object} center - New center coordinates {lat, lng}
   * @param {string} trigger - What triggered the pan ('user_drag', 'marker_click', 'search')
   */
  const trackMapPan = useCallback((center, trigger = 'user_drag') => {
    trackMapInteraction('pan', null, {
      center_lat: center.lat,
      center_lng: center.lng,
      trigger
    })
  }, [])

  /**
   * Track map bounds change
   * @param {Object} bounds - Map bounds
   */
  const trackMapBoundsChange = useCallback((bounds) => {
    trackMapInteraction('bounds_change', null, {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest()
    })
  }, [])

  return {
    trackMarkerClick,
    trackMapZoom,
    trackMapPan,
    trackMapBoundsChange
  }
}
